'use client';

import {
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@repo/ui';
import { ClipboardCheck, CreditCard, FolderTree, Loader2, Swords, Users } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { PaletteCommandDialog } from '@/components/command-palette';
import { CommandPaletteUserItem } from './command-palette-user-item';
import { useCommandPalette } from './use-command-palette';

const QUICK_LINKS = [
  { label: 'Collections', url: '/sys/collections', icon: FolderTree, shortcut: 'C' },
  { label: 'Challenges', url: '/sys/challenges', icon: Swords, shortcut: 'H' },
  { label: 'Users', url: '/sys/users', icon: Users, shortcut: 'U' },
  { label: 'Reviewers', url: '/sys/reviewers', icon: ClipboardCheck, shortcut: 'R' },
  { label: 'Billing', url: '/sys/billing', icon: CreditCard, shortcut: 'B' },
] as const;

const SHORTCUT_MAP = new Map(QUICK_LINKS.map((link) => [link.shortcut.toLowerCase(), link.url]));

export function SysCommandPalette(): React.JSX.Element | null {
  const pathname = usePathname();
  const router = useRouter();
  const isSysPage = pathname.startsWith('/sys');
  const { open, setOpen, search, setSearch, results, isSearching } = useCommandPalette(isSysPage);

  if (!isSysPage) {
    return null;
  }

  const navigate = (url: string): void => {
    router.push(url);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (search) {
      return;
    }
    const url = SHORTCUT_MAP.get(e.key.toLowerCase());
    if (url) {
      e.preventDefault();
      navigate(url);
    }
  };

  const quickLinks = QUICK_LINKS.filter((link) =>
    link.label.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <PaletteCommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Command Palette"
      showCloseButton={false}
    >
      <CommandInput
        placeholder="Type a command or search by email..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList onKeyDown={handleKeyDown}>
        {quickLinks.length > 0 && (
          <CommandGroup heading="Quick Links">
            {quickLinks.map((link) => (
              <CommandItem key={link.url} value={link.url} onSelect={() => navigate(link.url)}>
                <link.icon />
                {link.label}
                <CommandShortcut>{link.shortcut}</CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {search.length >= 3 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Users">
              {isSearching && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!isSearching && results.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">No users found.</p>
              )}
              {results.map((user) => (
                <CommandPaletteUserItem
                  key={user.id}
                  user={user}
                  onSelect={(id) => navigate(`/sys/users/${id}`)}
                />
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </PaletteCommandDialog>
  );
}
